/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import Highlight, { defaultProps } from 'prism-react-renderer';
import theme from 'prism-react-renderer/themes/vsDark';
import { colors, fancyBorder, prettyScrollBar } from '../../theme';
import { onMd } from '../../styles/responsive';
import CopyToClipboardSvg from '../../../static/icons/CopyToClipboard';
import { CSSProperties } from 'react';

type RendererToken = {
  types: string[];
  content: string;
  empty?: boolean;
};
const START_HIGHLIGHT_MARK = '{start-highlight}';
const STOP_HIGHLIGHT_MARK = '{stop-highlight}';

const START_IGNORE_SECTION = '{start-ignore}';
const STOP_IGNORE_SECTION = '{stop-ignore}';

const DIRECTIVE_TYPES = [START_HIGHLIGHT_MARK, STOP_HIGHLIGHT_MARK, START_IGNORE_SECTION, STOP_IGNORE_SECTION] as const;

const isNestedKeyAtLevel = ({
  token,
  tokenKey,
  line,
  level
}: {
  token: RendererToken;
  tokenKey: number;
  line: RendererToken[];
  level: number;
}) => {
  return (
    // tokenKey === 1 &&
    token.types.includes('key') &&
    !/^\s*$/.test(token.content) &&
    // /^\s*$/.test(line[tokenKey - 1].content) &&
    // line[tokenKey - 1].content.length === level * 2
    line.slice(0, tokenKey).reduce((finalLength, { content }) => finalLength + content.length, 0) === level * 2
  );
};

const BASH_COMMAND_TOKENS = ['stacktape', 'stp', 'yarn', 'npm'];

const addCustomStacktapeStyle = ({
  token,
  tokenKey,
  line,
  lang,
  prevToken
}: {
  token: RendererToken;
  tokenKey: number;
  line: RendererToken[];
  lang: string;
  prevToken?: RendererToken;
}): CSSProperties => {
  // console.log({ token, tokenKey, line, lang });
  if (lang === 'bash') {
    const tokenContent = token.content.trim();
    if (BASH_COMMAND_TOKENS.includes(tokenContent) && tokenKey === 0) {
      return { color: colors.primary, fontWeight: 'bold' };
    }
    if (tokenContent.startsWith('--') || tokenContent.startsWith('-')) {
      return { color: '#949494' };
    }
    if (tokenContent === '<<' || tokenContent === '>>' || prevToken?.content === '<<') {
      return { fontStyle: 'italic', color: colors.font };
    }
    return { color: colors.font };
  }
  if (lang === 'yaml' || lang === 'yml') {
    // highlight level 0 keys
    if (tokenKey === 0 || isNestedKeyAtLevel({ token, tokenKey, line, level: 0 })) {
      return { color: colors.primary, fontWeight: 'bold' };
    }
    // highlight level 1 keys
    if (isNestedKeyAtLevel({ token, tokenKey, line, level: 1 })) {
      return { color: colors.font, fontWeight: 'bold' };
    }
    // highlight level 2 keys
    if (isNestedKeyAtLevel({ token, tokenKey, line, level: 2 })) {
      return { color: '#dcdcaa' };
    }
    // adding styling to plain tokens
    // plain tokens are non-quoted strings, therefore same color as for strings is added
    if (token.types.length === 1 && token.types[0] === 'plain') {
      return { color: 'rgb(206, 145, 120)' };
    }
    // if one of the previous tokens in line was plain, then we are in non-quoted string
    // therefore the styling (color) should be same as for strings
    // this resolves problem with punctutation and numbers in non-quoted string
    if (
      line
        .slice(0, tokenKey)
        .some((pToken) => pToken.types.includes('plain') && !/^\s*$/.test(pToken.content) && pToken.types.length === 1)
    ) {
      return { color: 'rgb(206, 145, 120)' };
    }
  }
  return {};
};

const parseDirectiveCommentLine = ({ line }: { line: RendererToken[] }) => {
  for (const lineToken of line) {
    if (
      lineToken.types.includes('comment') &&
      DIRECTIVE_TYPES.find((directiveType) => lineToken.content.includes(directiveType))
    ) {
      return DIRECTIVE_TYPES.find((directiveType) => lineToken.content.includes(directiveType));
    }
  }
  // if (
  //   line.every(lineToken => {})

  //   /^\s*$/.test(line[0].content) &&
  //   line[1].types.includes('comment') &&
  //   DIRECTIVE_TYPES.find((directiveType) => line[1].content.includes(directiveType))
  //   // DIRECTIVE_TYPES.includes(line[1].content.slice(1).trim() as DirectiveType)
  // ) {
  //   return DIRECTIVE_TYPES.find((directiveType) => line[1].content.includes(directiveType));
  // }
};

export const copyToClipboard = (text: string) => {
  const strippedText = text
    .split('\n')
    .filter((line) => !line.includes(START_HIGHLIGHT_MARK) && !line.includes(STOP_HIGHLIGHT_MARK))
    .join('\n');
  const textarea = document.createElement('textarea');
  textarea.textContent = strippedText;
  textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in Microsoft Edge.
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy'); // Security exception may be thrown by some browsers.
  } catch (ex) {
    console.warn('Copy to clipboard failed.', ex);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

const CodeBlockModified = ({ children: code, ...props }) => {
  const lang = props.className ? props.className.split('-')[1] : null;
  const amountOfLines = code.split('\n').length;
  return (
    <div
      css={{
        ...fancyBorder,
        marginTop: '16px',
        marginBottom: '9px',
        overflow: 'auto',
        fontSize: amountOfLines <= 3 ? '14px !important' : '13px !important',
        lineHeight: '1.5'
      }}
    >
      <Highlight {...defaultProps} code={code} language={lang} theme={theme}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => {
          let applyHighlighting = false;
          let ignoreSection = false;
          return (
            <pre css={prettyScrollBar} className={className} style={style}>
              <div
                css={{
                  position: 'relative',
                  top: 0,
                  right: 0,
                  float: 'right',
                  display: 'none',
                  [onMd]: {
                    display: 'block'
                  }
                }}
                className="clipboard-box"
              >
                <span
                  css={{
                    cursor: 'pointer',
                    backgroundColor: 'inherit',
                    padding: '6px 8px'
                  }}
                  onClick={() => copyToClipboard(code)}
                >
                  <CopyToClipboardSvg />
                </span>
              </div>
              {tokens.map((line, i) => {
                // if (i === 0 && (line[0].content.startsWith('# desc') || line[0].content.startsWith('// desc'))) {
                //   console.log(line);
                // }
                const directive = parseDirectiveCommentLine({ line });
                if (directive) {
                  // when we encounter start-highlight directive we activate highlighting for successive lines
                  if (directive === START_HIGHLIGHT_MARK) {
                    applyHighlighting = true;
                  }
                  // highlighting is disabled once we encounter stop-highlight directive
                  if (directive === STOP_HIGHLIGHT_MARK) {
                    applyHighlighting = false;
                  }
                  if (directive === START_IGNORE_SECTION) {
                    ignoreSection = true;
                  }
                  if (directive === STOP_IGNORE_SECTION) {
                    ignoreSection = false;
                  }
                  // directive comments are discarded (they are not rendered)
                  return null;
                }
                if (ignoreSection) {
                  return null;
                }
                if (i !== tokens.length - 1) {
                  return (
                    <div
                      css={
                        applyHighlighting
                          ? { backgroundColor: colors.highlightedCodeLine, width: '100%', padding: '0px !important' }
                          : {}
                      }
                      {...getLineProps({ line, key: i })}
                    >
                      {line.map((token, key) => {
                        // we do this because bash coloring parses stacktape bash lines in a wrong way
                        if (
                          lang === 'bash' &&
                          (BASH_COMMAND_TOKENS.some((t) => token.content.split(' ')[0].trim().startsWith(t)) ||
                            token.content.trim().startsWith('-') ||
                            token.content.trim().startsWith('--'))
                        ) {
                          return token.content
                            .split(' ')
                            .map((t, idx) => ({ content: idx === 0 ? t : ` ${t}`, types: ['plain'] }))
                            .map((nestedToken) => {
                              const tokenProps = getTokenProps({
                                token: nestedToken,
                                key,
                                style: {
                                  ...addCustomStacktapeStyle({ token: nestedToken, tokenKey: key, line, lang })
                                }
                              });
                              return <span {...tokenProps} />;
                            });
                        }
                        const tokenProps = getTokenProps({
                          token,
                          key,
                          style: {
                            ...addCustomStacktapeStyle({
                              token,
                              tokenKey: key,
                              line,
                              lang,
                              prevToken: line[key - 1]
                            })
                          }
                        });
                        return <span {...tokenProps} />;
                      })}
                    </div>
                  );
                }
                return null;
              })}
            </pre>
          );
        }}
      </Highlight>
    </div>
  );
};

export default CodeBlockModified;
